import { Link } from "react-router-dom";
import { Target, Eye, Compass, ArrowUpRight } from "lucide-react";
import PageHead from "@/components/PageHead";
import Reveal, { SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const IMG =
  "https://images.unsplash.com/photo-1557804506-669a67965ba0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBJVCUyMGNvbnN1bHRhbnQlMjBidXNpbmVzcyUyMGV4ZWN1dGl2ZSUyMG1lZXRpbmclMjBvZmZpY2V8ZXwwfHx8MTc5MjM3MzI0MHww&ixlib=rb-4.1.0&q=85&auto=format&fit=crop&w=1400";

const VALUES = [
  {
    icon: Target,
    title: "Missão",
    text: "Entregar infraestrutura de TI corporativa estável, segura e escalável, por meio de consultoria técnica rigorosa e gestão responsável de ambientes críticos.",
  },
  {
    icon: Eye,
    title: "Visão",
    text: "Ser reconhecida pela excelência técnica em infraestrutura de TI, construindo relações de confiança duradouras com cada empresa atendida.",
  },
  {
    icon: Compass,
    title: "Valores",
    text: "Rigor técnico, transparência nas recomendações, compromisso com prazos, documentação completa e aprendizado contínuo.",
  },
];

const METHOD = [
  { step: "01", title: "Diagnóstico", desc: "Levantamento técnico do ambiente atual, riscos e gargalos." },
  { step: "02", title: "Arquitetura", desc: "Desenho da solução com dimensionamento e plano de ação." },
  { step: "03", title: "Implementação", desc: "Execução controlada, com testes e documentação." },
  { step: "04", title: "Gestão", desc: "Administração, monitoramento e suporte especializado." },
  { step: "05", title: "Evolução", desc: "Melhorias contínuas, modernização e otimização de recursos." },
];

export default function About() {
  return (
    <div>
      <PageHead
        title="Sobre a IPRS | Missão, Visão e Valores em Consultoria de TI"
        description="Conheça a IPRS Soluções em TI: nossa missão, visão e valores na consultoria e gestão de infraestrutura de TI corporativa."
        path="/sobre"
      />

      <section className="relative pt-28 lg:pt-40 pb-14 overflow-hidden">
        <div className="absolute inset-0 grid-lines" aria-hidden="true" />
        <div className="container-x relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-techblue">Sobre a IPRS</p>
              </Reveal>
              <Reveal delay={0.08}>
                <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-graphite leading-[1.15]">
                  Engenharia de TI focada em estabilidade, segurança e continuidade de negócios
                </h1>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-6 text-base lg:text-lg text-steel leading-relaxed max-w-2xl">
                  A IPRS Soluções em TI atua na concepção, implementação, administração e
                  modernização de infraestruturas corporativas. Unimos profundidade técnica a uma
                  postura consultiva: cada decisão parte do cenário real da empresa e das
                  necessidades concretas da operação.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.2}>
                <div className="rounded-xl overflow-hidden border border-line shadow-[0_24px_60px_-24px_rgba(37,44,51,0.35)]">
                  <img
                    src={IMG}
                    alt="Equipe de consultoria em reunião de planejamento de infraestrutura"
                    className="w-full h-[280px] sm:h-[340px] object-cover"
                    loading="lazy"
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-mist border-y border-line">
        <div className="container-x">
          <SectionHeading
            eyebrow="Nossos fundamentos"
            title="Missão, visão e valores"
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08} className="h-full">
                <div className="h-full bg-white border border-line rounded-xl p-8 hover:border-techblue/50 transition-colors duration-200">
                  <span className="w-11 h-11 rounded-lg bg-pale flex items-center justify-center text-techblue">
                    <v.icon className="w-5 h-5" />
                  </span>
                  <h3 className="mt-5 text-xl font-semibold text-graphite">{v.title}</h3>
                  <p className="mt-3 text-sm text-steel leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 lg:py-28">
        <div className="container-x">
          <SectionHeading
            eyebrow="Filosofia de trabalho"
            title="Rigor técnico, documentação e zero improviso"
            text="Ambientes críticos exigem método. Cada projeto segue padrões de engenharia e boas práticas reconhecidas de mercado, com registros completos para que a operação nunca dependa de conhecimento restrito a uma única pessoa."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {METHOD.map((m, i) => (
              <Reveal key={m.step} delay={i * 0.06} className="h-full">
                <div className="h-full border-t-2 border-techblue/70 pt-5">
                  <p className="font-mono text-sm text-techblue">{m.step}</p>
                  <h3 className="mt-2 text-base font-semibold text-graphite">{m.title}</h3>
                  <p className="mt-2 text-sm text-steel leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12">
              <Link
                to="/servicos"
                data-testid="about-services-link"
                className="inline-flex items-center gap-1.5 bg-techblue hover:bg-techblue-hover text-white text-sm font-semibold px-7 py-3.5 rounded-md transition-colors duration-200"
              >
                Conheça nossos serviços
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
